/**
 * Operator Detail Page
 *
 * Comprehensive view of a single operator with tabs for:
 * - Overview: Basic info and statistics
 * - Photos: Photo management with upload/delete
 * - Face Enrollment: AWS Rekognition integration
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Hash,
  Mail,
  Building2,
  Calendar,
  Camera,
  ClipboardCheck,
  TrendingUp,
  ScanFace,
  Upload,
  Trash2,
  Star,
  Pencil,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Link2,
  ImagePlus,
  Shield,
  Zap,
  Circle,
  Info,
  Sparkles,
  Clock,
  Briefcase,
  Check,
  Gauge,
  ExternalLink,
} from 'lucide-react';
import {
  useOperator,
  useOperatorPhotos,
  useRequestPhotoUpload,
  useDeletePhoto,
  useSetPrimaryPhoto,
  useEnrollFace,
  useRefreshPhotos,
  uploadToS3,
  useEnrollmentStatus,
  useEnrollAllPhotos,
  useRemoveAllEnrollments,
} from '../../hooks/useOperators';
import { useKpiMeasurements } from '../../hooks/useKpi';
import { OperatorForm } from '../../components/operators/OperatorForm';
import type { OperatorPhotoWithUrl } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';

type TabType = 'overview' | 'photos' | 'enrollment';

export function OperatorDetailPage() {
  useDocumentTitle('Operator Detail');
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Handle ?tab= query parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'photos', 'enrollment'].includes(tabParam)) {
      setActiveTab(tabParam as TabType);
      // Clear the query param after reading it
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch operator data
  const { data: operator, isLoading, error } = useOperator(id ?? null);

  if (isLoading) {
    return <LoadingState />;
  }

  if (error || !operator) {
    return <ErrorState />;
  }

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <User size={16} /> },
    { id: 'photos', label: 'Photos', icon: <Camera size={16} /> },
    { id: 'enrollment', label: 'Face Enrollment', icon: <ScanFace size={16} /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        {/* Back link */}
        <Link
          to="/operators"
          className="
            inline-flex items-center gap-2 text-sm font-medium text-gray-500
            transition-colors duration-200 hover:text-guild-green
            w-fit
          "
        >
          <ArrowLeft size={16} />
          Back to Operators
        </Link>

        {/* Title row */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                {operator.firstName} {operator.lastName}
              </h1>
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-mono font-semibold text-gray-600">
                <Hash size={12} />
                {operator.employeeId}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge isActive={operator.isActive} />
              <EnrollmentBadge isEnrolled={!!operator.rekognitionFaceId} />
            </div>
          </div>

          <button
            onClick={() => setIsEditOpen(true)}
            className="
              inline-flex items-center gap-2 rounded-xl
              border border-gray-200 bg-white px-4 py-2.5
              text-sm font-semibold text-gray-700
              shadow-sm transition-all duration-200
              hover:border-gray-300 hover:shadow-md
              active:scale-[0.98]
            "
          >
            <Pencil size={16} />
            Edit
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                relative flex items-center gap-2 pb-4 text-sm font-semibold
                transition-colors duration-200
                ${
                  activeTab === tab.id
                    ? 'text-guild-green'
                    : 'text-gray-500 hover:text-gray-700'
                }
              `}
            >
              {tab.icon}
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-guild-green" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && <OverviewTab operator={operator} />}
        {activeTab === 'photos' && <PhotosTab operatorId={operator.id} />}
        {activeTab === 'enrollment' && (
          <EnrollmentTab
            operatorId={operator.id}
            isEnrolled={!!operator.rekognitionFaceId}
          />
        )}
      </div>

      {/* Edit Modal */}
      {isEditOpen && (
        <OperatorForm
          operator={{
            id: operator.id,
            employeeId: operator.employeeId,
            firstName: operator.firstName,
            lastName: operator.lastName,
            email: operator.email,
            departmentId: operator.departmentId,
            departmentName: operator.department?.name ?? null,
            shiftId: operator.shiftId,
            shiftName: operator.shift?.name ?? null,
            positionId: operator.positionId,
            positionName: operator.position?.name ?? null,
            stationId: operator.stationId ?? null,
            stationName: operator.station?.name ?? null,
            isActive: operator.isActive,
            hasEnrolledFace: !!operator.rekognitionFaceId,
            photoCount: operator.photoCount,
            createdAt: operator.createdAt,
            // User linking data
            userId: operator.userId,
            linkedUser: operator.linkedUser,
          }}
          onClose={() => setIsEditOpen(false)}
        />
      )}
    </div>
  );
}

// ============================================
// OVERVIEW TAB
// ============================================

function OverviewTab({ operator }: { operator: NonNullable<ReturnType<typeof useOperator>['data']> }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Info Card */}
        <div className="lg:col-span-2 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
            Operator Information
          </h3>
          <dl className="grid gap-4 sm:grid-cols-2">
            <InfoItem
              icon={<Hash size={16} />}
              label="Employee ID"
              value={operator.employeeId}
              mono
            />
            <InfoItem
              icon={<User size={16} />}
              label="Full Name"
              value={`${operator.firstName} ${operator.lastName}`}
            />
            <InfoItem
              icon={<Mail size={16} />}
              label="Email"
              value={operator.email || '—'}
            />
            <InfoItem
              icon={<Building2 size={16} />}
              label="Department"
              value={operator.department?.name || 'Unassigned'}
            />
            <InfoItem
              icon={<Clock size={16} />}
              label="Shift"
              value={operator.shift?.name || 'Unassigned'}
              iconColor="text-blue-500"
            />
            <InfoItem
              icon={<Briefcase size={16} />}
              label="Position"
              value={operator.position?.name || 'Unassigned'}
              iconColor="text-purple-500"
            />
            <InfoItem
              icon={<Calendar size={16} />}
              label="Created"
              value={new Date(operator.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            />
            <InfoItem
              icon={<ScanFace size={16} />}
              label="Face Enrolled"
              value={operator.rekognitionFaceId ? 'Yes' : 'No'}
            />
            <InfoItem
              icon={<Link2 size={16} />}
              label="Linked User"
              value={
                operator.linkedUser
                  ? `${operator.linkedUser.fullName || 'Unnamed'} (${operator.linkedUser.email || '—'})`
                  : 'Not linked'
              }
            />
          </dl>
        </div>

        {/* Stats Cards */}
        <div className="space-y-4">
          <StatCard
            icon={<Camera size={20} />}
            label="Photos"
            value={operator.photoCount}
            color="blue"
          />
          <StatCard
            icon={<ClipboardCheck size={20} />}
            label="Assessments"
            value={operator.assessmentCount}
            color="violet"
          />
          <StatCard
            icon={<TrendingUp size={20} />}
            label="Avg. Score"
            value={operator.averageScore !== null ? `${operator.averageScore.toFixed(0)}%` : '—'}
            color="emerald"
          />
        </div>
      </div>

      {/* KPI History Section */}
      <KpiHistorySection operatorId={operator.id} />
    </div>
  );
}

// ============================================
// KPI HISTORY SECTION
// ============================================

function formatKpiValue(
  value: number | null | undefined,
  unit: string | null | undefined
): string {
  if (value === null || value === undefined) return '--';

  if (unit === 'seconds') {
    const minutes = Math.floor(value / 60);
    const seconds = Math.round(value % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  if (unit === 'percent' || unit === '%') {
    return `${value.toFixed(1)}%`;
  }

  if (unit === 'count' || Number.isInteger(value)) {
    return Math.round(value).toLocaleString();
  }

  return value.toFixed(2);
}

function getThresholdBadgeClasses(status: string | null | undefined): string {
  switch (status) {
    case 'normal':
      return 'bg-emerald-100 text-emerald-700';
    case 'warning':
      return 'bg-amber-100 text-amber-700';
    case 'critical':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function getThresholdDotColor(status: string | null | undefined): string {
  switch (status) {
    case 'normal':
      return 'bg-emerald-500';
    case 'warning':
      return 'bg-amber-500';
    case 'critical':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
}

function KpiHistorySection({ operatorId }: { operatorId: string }) {
  const { data, isLoading } = useKpiMeasurements({
    operatorId,
    limit: 20,
  });

  const measurements = data?.measurements ?? [];

  // Don't render if no measurements
  if (!isLoading && measurements.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-guild-green/10">
            <Gauge size={16} className="text-guild-green" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">KPI History</h3>
            <p className="text-sm text-gray-500">Recent KPI measurements for this operator</p>
          </div>
        </div>
        <Link
          to={`/analytics/kpi`}
          className="inline-flex items-center gap-1 text-sm font-medium text-guild-green hover:text-guild-green/80"
        >
          View All
          <ExternalLink size={14} />
        </Link>
      </div>

      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  KPI
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {measurements.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-3 text-sm font-medium text-gray-900">
                    {m.definition?.name ?? '--'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-3">
                    <span className="text-sm font-bold text-gray-900 tabular-nums">
                      {formatKpiValue(m.primaryValue, m.definition?.unit)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${getThresholdBadgeClasses(m.thresholdStatus)}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${getThresholdDotColor(m.thresholdStatus)}`} />
                      {m.thresholdStatus ?? 'Unknown'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-600">
                    {new Date(m.measuredAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="whitespace-nowrap px-6 py-3 text-right">
                    {(m as unknown as { triggerId?: string }).triggerId && (
                      <Link
                        to={`/assessments/triggers/${(m as unknown as { triggerId?: string }).triggerId}`}
                        className="inline-flex items-center gap-1 text-sm text-guild-green hover:text-guild-green/80"
                      >
                        <ExternalLink size={14} />
                        Trigger
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {measurements.length > 0 && (
        <div className="border-t border-gray-100 px-6 py-3">
          <span className="text-sm text-gray-500">
            Showing {measurements.length} of {data?.pagination?.total ?? 0} measurements
          </span>
        </div>
      )}
    </div>
  );
}

function InfoItem({
  icon,
  label,
  value,
  mono = false,
  iconColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
  iconColor?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50 ${iconColor || 'text-gray-400'}`}>
        {icon}
      </div>
      <div>
        <dt className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</dt>
        <dd className={`mt-0.5 text-sm font-medium text-gray-900 ${mono ? 'font-mono' : ''}`}>
          {value}
        </dd>
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: 'blue' | 'violet' | 'emerald';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    violet: 'bg-violet-50 text-violet-600',
    emerald: 'bg-emerald-50 text-emerald-600',
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PHOTOS TAB
// ============================================

function PhotosTab({ operatorId }: { operatorId: string }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data: photos, isLoading } = useOperatorPhotos(operatorId);
  const requestUpload = useRequestPhotoUpload();
  const deletePhoto = useDeletePhoto();
  const setPrimary = useSetPrimaryPhoto();
  const refreshPhotos = useRefreshPhotos();

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        setUploadError('Please select a JPEG, PNG, or WebP image');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('Image must be less than 10MB');
        return;
      }

      setUploading(true);
      setUploadError(null);

      try {
        // Get presigned URL
        const response = await requestUpload.mutateAsync({
          operatorId,
          data: {
            fileName: file.name,
            contentType: file.type as 'image/jpeg' | 'image/png' | 'image/webp',
          },
        });

        // Upload to S3
        const success = await uploadToS3(response.uploadUrl, file);
        if (!success) {
          throw new Error('Failed to upload to S3');
        }

        // Refresh photos
        refreshPhotos(operatorId);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [operatorId, requestUpload, refreshPhotos]
  );

  const handleDelete = useCallback(
    async (photoId: string) => {
      if (!confirm('Delete this photo?')) return;
      await deletePhoto.mutateAsync({ operatorId, photoId });
    },
    [operatorId, deletePhoto]
  );

  const handleSetPrimary = useCallback(
    async (photoId: string) => {
      await setPrimary.mutateAsync({ operatorId, photoId });
    },
    [operatorId, setPrimary]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-guild-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {photos?.length ?? 0} of 5 photos uploaded
        </p>
        <div className="flex items-center gap-3">
          {uploadError && (
            <span className="text-sm text-rose-500">{uploadError}</span>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading || (photos?.length ?? 0) >= 5}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || (photos?.length ?? 0) >= 5}
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-4 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/25
              transition-all duration-200
              hover:bg-guild-green/90
              disabled:cursor-not-allowed disabled:opacity-50
            "
          >
            {uploading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} />
                Upload Photo
              </>
            )}
          </button>
        </div>
      </div>

      {/* Photo Grid */}
      {!photos || photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-16">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
            <ImagePlus size={32} className="text-gray-400" />
          </div>
          <p className="mt-4 text-sm font-medium text-gray-900">No photos yet</p>
          <p className="mt-1 text-sm text-gray-500">
            Upload photos to enable face enrollment
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((photo) => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              onDelete={() => handleDelete(photo.id)}
              onSetPrimary={() => handleSetPrimary(photo.id)}
              isDeleting={deletePhoto.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoCard({
  photo,
  onDelete,
  onSetPrimary,
  isDeleting,
}: {
  photo: OperatorPhotoWithUrl;
  onDelete: () => void;
  onSetPrimary: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="aspect-square">
        <img
          src={photo.url}
          alt="Operator photo"
          className="h-full w-full object-cover"
        />
      </div>

      {/* Primary badge */}
      {photo.isPrimary && (
        <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-amber-400 px-2 py-1 text-xs font-semibold text-amber-900">
          <Star size={12} fill="currentColor" />
          Primary
        </div>
      )}

      {/* Hover overlay */}
      <div className="
        absolute inset-0 flex items-center justify-center gap-2
        bg-black/50 opacity-0 transition-opacity duration-200
        group-hover:opacity-100
      ">
        {!photo.isPrimary && (
          <button
            onClick={onSetPrimary}
            className="rounded-lg bg-white p-2 text-gray-700 transition-colors hover:bg-amber-100 hover:text-amber-700"
            title="Set as primary"
          >
            <Star size={18} />
          </button>
        )}
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="rounded-lg bg-white p-2 text-gray-700 transition-colors hover:bg-rose-100 hover:text-rose-700 disabled:opacity-50"
          title="Delete photo"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
}

// ============================================
// FACE ENROLLMENT TAB
// ============================================

// Quality configuration for enrollment status
const QUALITY_CONFIG = {
  excellent: {
    label: 'Excellent',
    color: 'emerald',
    bgColor: 'bg-emerald-500',
    lightBg: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    description: 'Maximum identification accuracy'
  },
  good: {
    label: 'Good',
    color: 'blue',
    bgColor: 'bg-blue-500',
    lightBg: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    description: 'Strong identification reliability'
  },
  fair: {
    label: 'Fair',
    color: 'yellow',
    bgColor: 'bg-yellow-500',
    lightBg: 'bg-yellow-50',
    textColor: 'text-yellow-600',
    borderColor: 'border-yellow-200',
    description: 'Consider adding more photos'
  },
  poor: {
    label: 'Poor',
    color: 'orange',
    bgColor: 'bg-orange-500',
    lightBg: 'bg-orange-50',
    textColor: 'text-orange-600',
    borderColor: 'border-orange-200',
    description: 'Add more photos for reliability'
  },
  not_enrolled: {
    label: 'Not Enrolled',
    color: 'gray',
    bgColor: 'bg-gray-300',
    lightBg: 'bg-gray-50',
    textColor: 'text-gray-500',
    borderColor: 'border-gray-200',
    description: 'Enroll photos to enable identification'
  },
} as const;

function EnrollmentTab({
  operatorId,
}: {
  operatorId: string;
  isEnrolled: boolean;
}) {
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());
  const [enrollmentResult, setEnrollmentResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Hooks
  const { data: enrollmentStatus, isLoading: isLoadingStatus } = useEnrollmentStatus(operatorId);
  const enrollFace = useEnrollFace();
  const enrollAllPhotos = useEnrollAllPhotos();
  const removeAllEnrollments = useRemoveAllEnrollments();

  // Derived state
  const quality = enrollmentStatus?.enrollmentQuality ?? 'not_enrolled';
  const qualityConfig = QUALITY_CONFIG[quality];
  const enrolledCount = enrollmentStatus?.enrolledPhotoCount ?? 0;
  const totalPhotos = enrollmentStatus?.photoCount ?? 0;
  const unenrolledCount = totalPhotos - enrolledCount;
  const progressPercent = Math.min((enrolledCount / 5) * 100, 100);

  // Toggle photo selection for multi-select
  const togglePhotoSelection = useCallback((photoId: string, isEnrolled: boolean) => {
    if (isEnrolled) return; // Can't select already-enrolled photos

    setSelectedPhotoIds(prev => {
      const next = new Set(prev);
      if (next.has(photoId)) {
        next.delete(photoId);
      } else {
        next.add(photoId);
      }
      return next;
    });
  }, []);

  // Handlers
  const handleEnrollSelected = useCallback(async () => {
    if (selectedPhotoIds.size === 0) return;

    setEnrollmentResult(null);

    // If only one photo selected, use single enroll
    if (selectedPhotoIds.size === 1) {
      const photoId = Array.from(selectedPhotoIds)[0];
      try {
        const result = await enrollFace.mutateAsync({
          operatorId,
          data: { photoId },
        });

        setEnrollmentResult({
          success: result.success,
          message: result.message ?? (result.success ? 'Photo enrolled successfully!' : 'Enrollment failed'),
        });

        if (result.success) {
          setSelectedPhotoIds(new Set());
        }
      } catch (err) {
        setEnrollmentResult({
          success: false,
          message: err instanceof Error ? err.message : 'Enrollment failed',
        });
      }
      return;
    }

    // Multiple photos - enroll all selected
    try {
      const result = await enrollAllPhotos.mutateAsync(operatorId);

      if (result.enrolled > 0) {
        setEnrollmentResult({
          success: true,
          message: `Successfully enrolled ${result.enrolled} photo${result.enrolled > 1 ? 's' : ''}${result.skipped > 0 ? `, ${result.skipped} skipped` : ''}`,
        });
        setSelectedPhotoIds(new Set());
      } else if (result.errors && result.errors.length > 0) {
        // Show first error message
        setEnrollmentResult({
          success: false,
          message: `Enrollment failed: ${result.errors[0].error}`,
        });
      } else if (result.skipped > 0) {
        setEnrollmentResult({
          success: true,
          message: `All ${result.skipped} photos were already enrolled`,
        });
      } else {
        setEnrollmentResult({
          success: false,
          message: 'No photos to enroll',
        });
      }
    } catch (err) {
      setEnrollmentResult({
        success: false,
        message: err instanceof Error ? err.message : 'Bulk enrollment failed',
      });
    }
  }, [selectedPhotoIds, operatorId, enrollFace, enrollAllPhotos]);

  const handleEnrollAll = useCallback(async () => {
    setEnrollmentResult(null);
    try {
      const result = await enrollAllPhotos.mutateAsync(operatorId);

      if (result.enrolled > 0) {
        setEnrollmentResult({
          success: true,
          message: `Successfully enrolled ${result.enrolled} photo${result.enrolled > 1 ? 's' : ''}${result.skipped > 0 ? `, ${result.skipped} skipped` : ''}`,
        });
        setSelectedPhotoIds(new Set()); // Clear selection on success
      } else if (result.errors && result.errors.length > 0) {
        // Show error from failed enrollments (e.g., AWS permission issues)
        setEnrollmentResult({
          success: false,
          message: `Enrollment failed: ${result.errors[0].error}`,
        });
      } else if (result.skipped > 0) {
        setEnrollmentResult({
          success: true,
          message: `All ${result.skipped} photos were already enrolled`,
        });
      } else {
        setEnrollmentResult({
          success: false,
          message: 'No photos to enroll',
        });
      }
    } catch (err) {
      setEnrollmentResult({
        success: false,
        message: err instanceof Error ? err.message : 'Bulk enrollment failed',
      });
    }
  }, [operatorId, enrollAllPhotos]);

  const handleRemoveAll = useCallback(async () => {
    if (!confirm('Remove all face enrollments? This operator will no longer be identified in videos until re-enrolled.')) return;

    setEnrollmentResult(null);
    try {
      const result = await removeAllEnrollments.mutateAsync(operatorId);
      setEnrollmentResult({
        success: true,
        message: result.message ?? `Removed ${result.removed} enrollment${result.removed !== 1 ? 's' : ''}`,
      });
    } catch (err) {
      setEnrollmentResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to remove enrollments',
      });
    }
  }, [operatorId, removeAllEnrollments]);

  const isProcessing = enrollFace.isPending || enrollAllPhotos.isPending || removeAllEnrollments.isPending;

  if (isLoadingStatus) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-guild-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enrollment Quality Card */}
      <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden shadow-sm">
        {/* Quality Header */}
        <div className="p-6 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`
                relative flex h-14 w-14 items-center justify-center rounded-2xl
                ${enrolledCount > 0 ? 'bg-gradient-to-br from-guild-green to-guild-green/80' : 'bg-gray-100'}
              `}>
                <Shield
                  size={28}
                  className={enrolledCount > 0 ? 'text-white' : 'text-gray-400'}
                  strokeWidth={1.5}
                />
                {enrolledCount > 0 && (
                  <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md">
                    <span className="text-xs font-bold text-guild-green">{enrolledCount}</span>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-gray-900">
                    Enrollment Quality
                  </h3>
                  <span className={`
                    inline-flex items-center gap-1 rounded-full px-2 py-0.5
                    text-xs font-semibold
                    ${qualityConfig.lightBg} ${qualityConfig.textColor}
                  `}>
                    {qualityConfig.label}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  {qualityConfig.description}
                </p>
              </div>
            </div>

            {/* Bulk Actions */}
            {totalPhotos > 0 && (
              <div className="flex items-center gap-2">
                {unenrolledCount > 0 && (
                  <button
                    onClick={handleEnrollAll}
                    disabled={isProcessing}
                    className="
                      inline-flex items-center gap-2 rounded-xl
                      bg-guild-green px-4 py-2.5
                      text-sm font-semibold text-white
                      shadow-lg shadow-guild-green/25
                      transition-all duration-200
                      hover:bg-guild-green/90 hover:shadow-xl
                      disabled:cursor-not-allowed disabled:opacity-50
                    "
                  >
                    {enrollAllPhotos.isPending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Enrolling...
                      </>
                    ) : (
                      <>
                        <Zap size={16} />
                        Enroll All ({unenrolledCount})
                      </>
                    )}
                  </button>
                )}
                {enrolledCount > 0 && (
                  <button
                    onClick={handleRemoveAll}
                    disabled={isProcessing}
                    className="
                      inline-flex items-center gap-2 rounded-xl
                      border border-gray-200 bg-white px-4 py-2.5
                      text-sm font-semibold text-gray-600
                      transition-all duration-200
                      hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600
                      disabled:cursor-not-allowed disabled:opacity-50
                    "
                  >
                    {removeAllEnrollments.isPending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <XCircle size={16} />
                    )}
                    Remove All
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pb-4">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
            <span className="font-medium">
              {enrolledCount} of {totalPhotos} photo{totalPhotos !== 1 ? 's' : ''} enrolled
            </span>
            <span>
              {enrolledCount >= 5 ? <span className="inline-flex items-center gap-0.5 text-emerald-600"><Check size={12} strokeWidth={2.5} /> Optimal</span> : `${5 - enrolledCount} more for optimal`}
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${qualityConfig.bgColor}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {/* Progress markers */}
          <div className="relative h-1 mt-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <div
                key={n}
                className="absolute top-0 w-0.5 h-1 bg-gray-300"
                style={{ left: `${(n / 5) * 100}%`, transform: 'translateX(-50%)' }}
              />
            ))}
          </div>
        </div>

        {/* Helper Info */}
        <div className="px-6 pb-6">
          <div className="flex items-start gap-2 rounded-xl bg-guild-mint/20 px-4 py-3">
            <Info size={16} className="text-guild-green flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-600 leading-relaxed">
              <span className="font-semibold text-guild-green">3-5 photos recommended</span> for best identification accuracy.
              Multiple angles and lighting conditions improve face recognition reliability.
            </p>
          </div>
        </div>
      </div>

      {/* Result Message */}
      {enrollmentResult && (
        <div className={`
          flex items-center gap-3 rounded-xl px-4 py-3 animate-in slide-in-from-top-2 duration-300
          ${enrollmentResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}
        `}>
          {enrollmentResult.success ? (
            <CheckCircle2 size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span className="text-sm font-medium">{enrollmentResult.message}</span>
        </div>
      )}

      {/* Photos Grid */}
      {totalPhotos === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-16">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
            <Camera size={32} className="text-gray-400" />
          </div>
          <p className="mt-4 text-sm font-medium text-gray-900">No photos available</p>
          <p className="mt-1 text-sm text-gray-500 text-center max-w-xs">
            Upload photos in the Photos tab first, then return here to enroll faces for identification.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400">
              Photo Enrollment Status
            </h3>
            {selectedPhotoIds.size > 0 && unenrolledCount > 0 && (
              <button
                onClick={handleEnrollSelected}
                disabled={isProcessing}
                className="
                  inline-flex items-center gap-2 rounded-lg
                  bg-guild-green/10 px-3 py-1.5
                  text-xs font-semibold text-guild-green
                  transition-all duration-200
                  hover:bg-guild-green/20
                  disabled:opacity-50
                "
              >
                {enrollFace.isPending || enrollAllPhotos.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <ScanFace size={14} />
                )}
                Enroll Selected ({selectedPhotoIds.size})
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {enrollmentStatus?.photos.map((photo) => (
              <EnrollmentPhotoCard
                key={photo.id}
                photo={photo}
                isSelected={selectedPhotoIds.has(photo.id)}
                onSelect={() => togglePhotoSelection(photo.id, photo.isEnrolled)}
                disabled={isProcessing}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-component for photo cards with enrollment status
function EnrollmentPhotoCard({
  photo,
  isSelected,
  onSelect,
  disabled,
}: {
  photo: {
    id: string;
    isPrimary: boolean;
    isEnrolled: boolean;
    enrolledAt: Date | null;
    url: string;
  };
  isSelected: boolean;
  onSelect: () => void;
  disabled: boolean;
}) {
  const enrolledDate = photo.enrolledAt
    ? new Date(photo.enrolledAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <button
      onClick={onSelect}
      disabled={disabled || photo.isEnrolled}
      className={`
        group relative aspect-square overflow-hidden rounded-xl border-2
        transition-all duration-200
        ${photo.isEnrolled
          ? 'border-emerald-200 cursor-default'
          : isSelected
            ? 'border-guild-green ring-2 ring-guild-green/20'
            : 'border-gray-100 hover:border-gray-200'
        }
        ${disabled ? 'opacity-60' : ''}
      `}
    >
      <img
        src={photo.url}
        alt="Operator photo"
        className="h-full w-full object-cover"
      />

      {/* Enrollment Status Badge */}
      <div className="absolute top-2 left-2">
        {photo.isEnrolled ? (
          <span className="
            inline-flex items-center gap-1 rounded-full
            bg-emerald-500 px-2 py-0.5
            text-[10px] font-bold text-white
            shadow-sm
          ">
            <CheckCircle2 size={10} />
            Enrolled
          </span>
        ) : (
          <span className="
            inline-flex items-center gap-1 rounded-full
            bg-gray-800/60 backdrop-blur-sm px-2 py-0.5
            text-[10px] font-medium text-white
          ">
            <Circle size={8} />
            Not Enrolled
          </span>
        )}
      </div>

      {/* Primary Badge */}
      {photo.isPrimary && (
        <div className="absolute top-2 right-2">
          <span className="
            inline-flex items-center gap-1 rounded-full
            bg-amber-400 px-2 py-0.5
            text-[10px] font-bold text-amber-900
            shadow-sm
          ">
            <Star size={10} fill="currentColor" />
            Primary
          </span>
        </div>
      )}

      {/* Selection Overlay for Unenrolled */}
      {!photo.isEnrolled && isSelected && (
        <div className="absolute inset-0 flex items-center justify-center bg-guild-green/20">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-guild-green shadow-lg">
            <CheckCircle2 size={24} className="text-white" />
          </div>
        </div>
      )}

      {/* Hover Overlay for Enrolled - Show Date */}
      {photo.isEnrolled && enrolledDate && (
        <div className="
          absolute inset-x-0 bottom-0
          bg-gradient-to-t from-black/70 to-transparent
          px-3 py-2
          opacity-0 group-hover:opacity-100
          transition-opacity duration-200
        ">
          <p className="text-[10px] text-white/90">
            <Sparkles size={10} className="inline mr-1" />
            Enrolled {enrolledDate}
          </p>
        </div>
      )}

      {/* Click Hint for Unenrolled */}
      {!photo.isEnrolled && !isSelected && !disabled && (
        <div className="
          absolute inset-0 flex items-center justify-center
          bg-black/40 opacity-0 group-hover:opacity-100
          transition-opacity duration-200
        ">
          <span className="text-xs font-medium text-white">
            Click to select
          </span>
        </div>
      )}
    </button>
  );
}

// ============================================
// SHARED COMPONENTS
// ============================================

function StatusBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Inactive
    </span>
  );
}

function EnrollmentBadge({ isEnrolled }: { isEnrolled: boolean }) {
  return isEnrolled ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
      <ScanFace size={12} />
      Face Enrolled
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
      <ScanFace size={12} />
      Not Enrolled
    </span>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-24">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-sm text-gray-500">Loading operator...</p>
    </div>
  );
}

function ErrorState() {
  return (
    <div className="flex flex-col items-center justify-center py-24">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50">
        <AlertCircle size={32} className="text-rose-500" />
      </div>
      <p className="mt-4 text-sm font-medium text-gray-900">Operator not found</p>
      <p className="mt-1 text-sm text-gray-500">
        This operator may have been deleted or you don't have access.
      </p>
      <Link
        to="/operators"
        className="
          mt-4 inline-flex items-center gap-2 rounded-xl
          bg-guild-green px-4 py-2.5
          text-sm font-semibold text-white
          transition-colors hover:bg-guild-green/90
        "
      >
        <ArrowLeft size={16} />
        Back to Operators
      </Link>
    </div>
  );
}

export default OperatorDetailPage;
